import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Fire, Trophy, Tag, CaretDown, CalendarCheck } from "@phosphor-icons/react";
import { HBar, DonutChart, formatWeekLabel } from "./InsightsCharts";

const STATUS_COLORS = {
  draft: "#6B7280",
  in_review: "#F59E0B",
  approved: "#10B981",
  published: "#3B82F6",
};

export function PlanThisMonthCTA() {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
      <Link to="/plans/new" className="flex items-center justify-between p-4 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg hover:border-[#FF4400]/30 transition-colors group" data-testid="plan-this-month-cta">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#FF4400]/10">
            <CalendarCheck size={20} className="text-[#FF4400]" weight="fill" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[var(--text-primary)]">Plan This Month</p>
            <p className="text-xs text-[var(--text-muted)]">Get an AI-generated content strategy tailored to your goals</p>
          </div>
        </div>
        <span className="text-xs font-medium text-[#FF4400] group-hover:translate-x-0.5 transition-transform">&rarr;</span>
      </Link>
    </motion.div>
  );
}

export function ContentTypeChart({ byContentType }) {
  if (!byContentType?.length) return null;
  const maxCount = Math.max(...byContentType.map((c) => c.count));
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5"
      data-testid="content-type-chart"
    >
      <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">What You're Creating Most</h2>
      <div className="space-y-2.5">
        {byContentType.map((ct) => (
          <HBar key={ct.content_type} label={ct.content_type} value={ct.count} max={maxCount} count={ct.count} />
        ))}
      </div>
    </motion.div>
  );
}

export function WeeklyActivityChart({ byWeek }) {
  if (!byWeek?.length) return null;
  const maxW = Math.max(...byWeek.map((w) => w.count));
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5"
      data-testid="weekly-activity-chart"
    >
      <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">Your Generation Activity</h2>
      <div className="flex items-end gap-1 h-40">
        {byWeek.map((w, i) => (
          <div key={w.week_start} className="flex-1 flex flex-col items-center gap-1 min-w-0">
            <span className="text-[9px] font-['JetBrains_Mono'] text-[var(--text-muted)]">{w.count}</span>
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${maxW > 0 ? (w.count / maxW) * 100 : 0}%` }}
              transition={{ delay: 0.3 + i * 0.03, duration: 0.4 }}
              className="w-full rounded-t bg-gradient-to-t from-[#FF4400] to-[#FF6633] min-h-[2px]"
            />
            <span className="text-[8px] text-[var(--text-muted)] truncate w-full text-center">{formatWeekLabel(w.week_start)}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export function BusinessStatusChart({ byBusiness, statusBreakdown }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5"
      data-testid="business-status-chart"
    >
      {byBusiness?.length > 1 ? (
        <>
          <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">Activity by Client</h2>
          <DonutChart segments={byBusiness.map((b) => ({ label: b.business_name, count: b.count }))} />
        </>
      ) : (
        <>
          <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">Content Status</h2>
          <div className="space-y-3">
            {statusBreakdown?.map((sb) => (
              <div key={sb.status} className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLORS[sb.status] || "#6B7280" }} />
                <span className="text-sm text-[var(--text-secondary)] flex-1 capitalize">{sb.status.replace("_", " ")}</span>
                <span className="text-sm font-['JetBrains_Mono'] text-[var(--text-primary)]">{sb.count}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
}

export function TopTopicsSection({ topTopics }) {
  if (!topTopics || topTopics.length < 3) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35 }}
      className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5"
      data-testid="top-topics"
    >
      <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">Your Most-Generated Topics</h2>
      <div className="flex flex-wrap gap-2">
        {topTopics.map((t, i) => (
          <span
            key={t.topic}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-all"
            style={{
              backgroundColor: i === 0 ? "rgba(255,68,0,0.1)" : "var(--bg-hover)",
              borderColor: i === 0 ? "rgba(255,68,0,0.2)" : "var(--border)",
              color: i === 0 ? "#FF4400" : "var(--text-secondary)",
            }}
          >
            <Tag size={12} />
            <span className="truncate max-w-[200px]">{t.topic}</span>
            <span className="font-['JetBrains_Mono'] text-[10px] opacity-60">{t.count}</span>
          </span>
        ))}
      </div>
    </motion.div>
  );
}

export function AchievementsSection({ achievementsOpen, setAchievementsOpen, user }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.4 }}
      className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg overflow-hidden"
      data-testid="achievements-section"
    >
      <button
        onClick={() => setAchievementsOpen(!achievementsOpen)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
        data-testid="achievements-toggle"
      >
        <div className="flex items-center gap-2">
          <Trophy size={16} className="text-[var(--badge-pink)]" weight="fill" />
          <span className="text-sm font-medium text-[var(--text-secondary)]">Achievements</span>
        </div>
        <CaretDown size={14} className={`text-[var(--text-muted)] transition-transform ${achievementsOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {achievementsOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 flex flex-wrap gap-4">
              <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[var(--bg-base)] border border-[var(--border)]">
                <Trophy size={20} weight="fill" className="text-[var(--badge-pink)]" />
                <div>
                  <p className="text-sm font-semibold text-[var(--text-primary)]">Level {user?.level || 1}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="w-20 h-1.5 bg-[var(--bg-hover)] rounded-full overflow-hidden">
                      <div className="h-full bg-[var(--success)] rounded-full" style={{ width: `${(user?.xp || 0) % 100}%` }} />
                    </div>
                    <span className="text-[10px] font-['JetBrains_Mono'] text-[var(--success)]">{user?.xp || 0} XP</span>
                  </div>
                </div>
              </div>
              {(user?.streak || 0) > 0 && (
                <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[var(--bg-base)] border border-[var(--border)]">
                  <Fire size={20} weight="fill" className="text-[#FF4400]" />
                  <div>
                    <p className="text-sm font-semibold text-[var(--text-primary)]">{user.streak}-Day Streak</p>
                    <p className="text-[10px] text-[var(--text-muted)] mt-0.5">Keep generating daily!</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
